import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Logo from "@/components/Logo";

const easeOut = [0.19, 1, 0.22, 1];

// A photo counts as "real" only when it isn't empty and isn't one of our
// placeholder stock URLs. Unsplash / placeholder / example URLs are treated as
// "no photo yet" so the About page stays text-only until the brand owners
// upload authentic portraits through the Admin Panel.
const isRealPhoto = (url) => {
  if (!url || typeof url !== "string") return false;
  const u = url.toLowerCase().trim();
  if (!u) return false;
  if (u.includes("unsplash")) return false;
  if (u.includes("placeholder")) return false;
  if (u.includes("example.com")) return false;
  return true;
};

const VALUES = [
  {
    key: "quality",
    label: "Quality",
    body: "Heavyweight cottons, considered stitching, garments made to outlast trends.",
  },
  {
    key: "design",
    label: "Design",
    body: "Silhouettes drawn with restraint. A palette limited to black, white and everything essential.",
  },
  {
    key: "community",
    label: "Community",
    body: "Built with the people who wear us — every drop is shaped by real conversations.",
  },
];

export default function About() {
  const [founders, setFounders] = useState([]);

  useEffect(() => {
    api.get("/founders").then(({ data }) => setFounders(data)).catch(() => {});
  }, []);

  const withPhotos = founders.filter((f) => isRealPhoto(f.photo));

  return (
    <div data-testid="about-page">
      {/* Logo hero */}
      <section className="border-b py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: easeOut }}
          >
            <Logo variant="full" className="h-40 md:h-56 w-auto dark:invert" loading="eager" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6, ease: easeOut }}
            className="mt-10 label-xs opacity-60"
          >
            Our Story
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: easeOut }}
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tighter leading-[1.02]"
          >
            Built by Friends.<br />Driven by Passion.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: easeOut }}
            className="mt-8 max-w-xl text-sm sm:text-base opacity-70 leading-relaxed"
          >
            THE FABRICS began as a shared idea between four friends — a belief that clothing should
            feel considered, uncompromising and quietly confident. No noise. No filler. Only the pieces
            we wanted to wear ourselves.
          </motion.p>
        </div>
      </section>

      {/* Founders — text-only until real photos are uploaded */}
      <section
        className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24 lg:py-32"
        data-testid="founders-section"
      >
        <div className="text-center">
          <p className="label-xs opacity-60">The Founders</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
            Four names.<br />One label.
          </h2>
        </div>

        <ul className="mt-16 lg:mt-20 max-w-2xl mx-auto divide-y">
          {founders.map((f, i) => (
            <motion.li
              key={f.id}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: easeOut, delay: i * 0.08 }}
              className="py-6 lg:py-7 flex items-baseline gap-6"
              data-testid={`founder-row-${f.id}`}
            >
              <span className="label-xs opacity-40 tabular-nums w-8">
                {String(f.order || i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6">
                <span className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight">
                  {f.name}
                </span>
                <span className="text-xs sm:text-sm opacity-60 tracking-wide uppercase">
                  {f.role}
                </span>
              </div>
            </motion.li>
          ))}
        </ul>

        {withPhotos.length > 0 && (
          <div
            className="mt-20 lg:mt-28 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10"
            data-testid="founders-photo-grid"
          >
            {withPhotos.map((f, i) => (
              <motion.figure
                key={`photo-${f.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: easeOut, delay: i * 0.08 }}
                className="group"
                data-testid={`founder-card-${f.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-muted">
                  <img
                    src={f.photo}
                    alt={f.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <figcaption className="mt-5">
                  <h3 className="font-serif text-xl tracking-tight leading-tight">{f.name}</h3>
                  <p className="mt-1 text-xs opacity-60 tracking-wide uppercase">{f.role}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}
      </section>

      {/* Values */}
      <section className="border-t" data-testid="values-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-2xl">
            <p className="label-xs opacity-60">What we stand for</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
              Our values.
            </h2>
          </div>
          <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-y-14 md:gap-x-14">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease: easeOut, delay: i * 0.08 }}
                className="border-t pt-8"
                data-testid={`value-${v.key}`}
              >
                <p className="label-xs opacity-40 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight uppercase">
                  {v.label}
                </h3>
                <p className="mt-5 text-sm opacity-70 leading-relaxed max-w-xs">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing quote */}
      <section className="border-t py-24 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: easeOut }}
          className="max-w-3xl mx-auto px-6 text-center"
          data-testid="about-closing-quote"
        >
          <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
            "Four Friends.<br />One Vision.<br />Infinite Style."
          </p>
          <div className="mt-14 flex flex-col items-center">
            <Logo variant="monogram" className="h-10 w-auto dark:invert" />
            <div className="mt-4 font-display tracking-[0.4em] text-xs">THE FABRICS</div>
            <div className="mt-2 text-[10px] tracking-[0.5em] opacity-60">WEAR YOUR IDENTITY.</div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
